// The hub's version in the sidebar footer; clicking it shows what changed in each release (changelog.ts).
import { useState } from 'react'
import { createPortal } from 'react-dom'
import { RELEASE_NOTES } from '../changelog'
import { can, useAuth } from '../lib/auth'
import { useLoadedVersion } from '../lib/update-check'
import { Badge, Button, Modal } from '../ui'

const day = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })

export function VersionLink() {
  const version = useLoadedVersion()
  const [open, setOpen] = useState(false)
  if (!version || version === 'dev') return null
  return (
    <>
      <button type="button" className="side-version" onClick={() => setOpen(true)} title="See what’s changed in each release">
        Version {version}
      </button>
      {/* At page level: inside the sidebar footer it took the footer's faint text and sat under the page's sticky headers. */}
      {open && createPortal(<ReleaseNotes current={version} onClose={() => setOpen(false)} />, document.body)}
    </>
  )
}

function ReleaseNotes({ current, onClose }: { current: string; onClose: () => void }) {
  const { me } = useAuth()
  // Only the changes to areas this person can use; a release with none of those is left out.
  const notes = RELEASE_NOTES.map((r) => ({
    ...r,
    changes: r.changes.flatMap((c) => (typeof c === 'string' ? [c] : can(me, c.perm) ? [c.text] : [])),
  })).filter((r) => r.changes.length > 0)
  return (
    <Modal className="modal-body modal-wide" onClose={onClose} labelledBy="release-notes-title">
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h2 id="release-notes-title">What’s new in Briary Hub</h2>
        <Button onClick={onClose}>Close</Button>
      </div>
      <p className="muted">
        You’re using version <strong className="num">{current}</strong>. The newest changes are at the top.
      </p>
      <div className="release-notes">
        {notes.map((r) => (
          <section key={r.version} className={`release-note${r.version === current ? ' current' : ''}`}>
            <div className="release-note-head">
              <strong>{r.title}</strong>
              {r.version === current && <Badge tone="good">Your version</Badge>}
            </div>
            <div className="muted num release-note-meta">
              Version {r.version} · {day(r.date)}
            </div>
            <ul>
              {r.changes.map((c, i) => (
                <li key={i}>{c}</li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </Modal>
  )
}
